/* SSi website – Intensive learning cross-link card.
import React from 'react';
 *
 * Reusable cross-link card that surfaces the intensive-learning hub
 * from anywhere on the site. Same visual: a fanned stack of three
 * video thumbnails on the left, an eyebrow/headline/sub block in
 * the middle, and an arrow CTA on the right.
 *
 * Usage:
 *   <IntensiveCrossLink variant="home"/>
 *   <IntensiveCrossLink variant="welsh"/>
 *   <IntensiveCrossLink variant="testimonials" bg="var(--offWhite)"/>
 *
 * Variants live in ICL_VARIANTS – copy + thumbnail mix tuned for
 * the surrounding page so the link reads as a natural extension
 * of the section's argument, not a generic ad.
 *
 * The methodology-page cards use their own inline component
 * (StageIntensiveLink) – this one is for full-width page bands.
 */

import { Link } from 'react-router-dom';

/* Inject one-time CSS for hover, live-dot pulse, and mobile stack. */
(function injectIclStyles() {
  if (typeof document === 'undefined') return;
  if (document.getElementById('ssi-icl-styles')) return;
  const style = document.createElement('style');
  style.id = 'ssi-icl-styles';
  style.textContent = `
    .ssi-icl {
      transition: transform .18s var(--ease-out, ease-out),
                  box-shadow .18s var(--ease-out, ease-out),
                  border-color .18s var(--ease-out, ease-out);
    }
    .ssi-icl:hover {
      transform: translateY(-2px);
      box-shadow: var(--shadow-md);
      border-color: rgba(219,30,23,.35);
    }
    .ssi-icl:hover .ssi-icl__arrow { transform: translateX(4px); }
    .ssi-icl__arrow { transition: transform .2s var(--ease-out, ease-out); display:inline-block; }
    @keyframes ssiIclPulse {
      0%   { box-shadow: 0 0 0 0 rgba(219,30,23,.55); }
      70%  { box-shadow: 0 0 0 8px rgba(219,30,23,0); }
      100% { box-shadow: 0 0 0 0 rgba(219,30,23,0); }
    }
    .ssi-icl__liveDot { animation: ssiIclPulse 1.6s infinite; }
    @media (max-width: 720px) {
      .ssi-icl { grid-template-columns: 1fr !important; gap: 16px !important; padding: 20px !important; }
      .ssi-icl__thumbs { width: 132px !important; height: 56px !important; padding-left: 28px !important; }
      .ssi-icl__thumbs > div { width: 92px !important; height: 56px !important; }
      .ssi-icl__ctaCol { padding-left: 0 !important; }
      .ssi-icl-inline { grid-template-columns: 1fr !important; gap: 20px !important; padding: 20px !important; }
      .ssi-icl-inline__player { max-width: 100% !important; }
    }
    .ssi-icl-inline { transition: box-shadow .18s var(--ease-out, ease-out); }
    .ssi-icl-inline__poster:hover .ssi-icl-inline__playBtn { transform: translate(-50%, -50%) scale(1.06); background: var(--brandRed); }
    .ssi-icl-inline__playBtn { transition: transform .2s var(--ease-out, ease-out), background .2s var(--ease-out, ease-out); }
  `;
  document.head.appendChild(style);
})();

const ICL_THUMBS = {
  japanuary: 'https://i.ytimg.com/vi/EWS0YlmdGZg/hqdefault.jpg',
  ireland:   'https://i.ytimg.com/vi/3ERA8ctyYPg/hqdefault.jpg',
  tom:       'https://i.ytimg.com/vi/BrL0OaUHALM/hqdefault.jpg',
  croatia:   'https://i.ytimg.com/vi/sqAChFizLiI/hqdefault.jpg',
  welsh5day: 'https://i.ytimg.com/vi/UlRdBHzbHdY/hqdefault.jpg',
};

const ICL_VARIANTS = {
  /* Homepage – first-time visitor, narrative hook. */
  home: {
    eyebrow: 'See it in action',
    headline: 'Real people. Pushing the method to its limits. On camera.',
    sub: "Five intensives we've filmed in full – sprints and marathons, beginners and returners. The most detailed proof we have.",
    cta: 'Watch the intensives',
    thumbs: ['tom', 'japanuary', 'croatia'],
  },

  /* Welsh language landing – plays the October 2017 Welsh bootcamp inline. */
  welsh: {
    eyebrow: 'Welsh from scratch · on tape',
    headline: 'Three learners. Five days. Before and after, on tape.',
    sub: 'October 2017: a five-day Welsh bootcamp filmed in full – day-one expectations, then the same people speaking Welsh on day five.',
    inlineVideo: { id: '29hD3ZKwEqo', title: 'Five days of Welsh – October 2017 bootcamp' },
  },

  /* Japanese language landing – plays Japanuary inline. */
  japanese: {
    eyebrow: 'Japanese from scratch · on tape',
    headline: 'Five of us. Ten days. None of us spoke Japanese on day one.',
    sub: 'Japanuary 2025: eight hours a day, filmed in full. Watch what ten days of intensive practice can do.',
    inlineVideo: { id: 'EWS0YlmdGZg', title: 'Japanuary 2025' },
  },

  /* Generic language landing – Spanish/French/German/Italian/Korean/Mandarin/Portuguese/Arabic. */
  language: {
    eyebrow: 'See it in action',
    headline: 'What does intensive learning actually look like?',
    sub: "We've filmed five learners – including total beginners – running real intensives in real languages. Unedited.",
    cta: 'Watch the intensives',
    thumbs: ['tom', 'japanuary', 'croatia'],
  },

  /* Testimonials page – "and these are the ones on tape". */
  testimonials: {
    eyebrow: 'On camera, not just on paper',
    headline: 'And these are the stories with the cameras rolling.',
    sub: 'Five filmed intensives – beginners going from zero to first conversations, unedited from day one.',
    cta: 'See it on tape',
    thumbs: ['japanuary', 'tom', 'ireland'],
  },

  /* Pricing – reframe the spend. */
  pricing: {
    eyebrow: "What's possible",
    headline: 'People have learned a language in 30 days using this same platform.',
    sub: 'Five filmed intensives on the hub. The £15-a-month subscription is the same one they used.',
    cta: 'See what it can do',
    thumbs: ['tom', 'japanuary', 'croatia'],
  },
};

function IntensiveCrossLink({
  variant = 'home',
  bg = 'var(--offWhite)',
  pad = '64px 32px',
  label,
  override,
}) {
  const v = { ...(ICL_VARIANTS[variant] || ICL_VARIANTS.home), ...(override || {}) };
  if (v.inlineVideo) return <IclInlineVideo v={v} bg={bg} pad={pad} label={label}/>;
  return <IclLinkCard v={v} bg={bg} pad={pad} label={label}/>;
}

function IclLinkCard({ v, bg, pad, label }) {
  const thumbs = (v.thumbs || []).map(k => ICL_THUMBS[k] || k);
  return (
    <section
      data-screen-label={label || 'See it in action'}
      style={{ background: bg, padding: pad, borderTop: '1px solid var(--border)', borderBottom: '1px solid var(--border)' }}
    >
      <Link
        to="/intensive-learning-hub"
        className="ssi-icl"
        style={iclStyles.card}
      >
        <div className="ssi-icl__thumbs" style={iclStyles.thumbs} aria-hidden="true">
          {thumbs.map((src, i) => (
            <div key={i} style={{
              ...iclStyles.thumb,
              transform: `translateX(${i * -18}px) rotate(${(i - 1) * -2.5}deg)`,
              zIndex: 3 - i,
            }}>
              <img src={src} alt="" style={iclStyles.thumbImg}/>
              {i === 0 && (
                <span style={iclStyles.play}>
                  <svg viewBox="0 0 24 24" width="12" height="12"><path d="M8 5v14l11-7z" fill="#fff"/></svg>
                </span>
              )}
            </div>
          ))}
        </div>
        <div style={iclStyles.body}>
          <div style={iclStyles.eyebrowRow}>
            <span style={iclStyles.eyebrow}>{v.eyebrow}</span>
            {v.live && (
              <span style={iclStyles.live}>
                <span className="ssi-icl__liveDot" style={iclStyles.liveDot} aria-hidden="true"/>
                Live now
              </span>
            )}
          </div>
          <div style={iclStyles.headline}>{v.headline}</div>
          <div style={iclStyles.sub}>{v.sub}</div>
        </div>
        <div className="ssi-icl__ctaCol" style={iclStyles.ctaCol}>
          <span style={iclStyles.cta}>{v.cta}</span>
          <span className="ssi-icl__arrow" style={iclStyles.arrow}>→</span>
        </div>
      </Link>
    </section>
  );
}

const iclStyles = {
  card: {
    display: 'grid',
    gridTemplateColumns: 'auto 1fr auto',
    gap: 28,
    alignItems: 'center',
    background: '#fff',
    border: '1px solid var(--border)',
    borderRadius: 14,
    padding: '22px 26px',
    maxWidth: 880,
    margin: '0 auto',
    boxShadow: 'var(--shadow-sm)',
    textDecoration: 'none',
    color: 'inherit',
  },
  thumbs: { position: 'relative', width: 148, height: 64, flexShrink: 0, paddingLeft: 36 },
  thumb: {
    position: 'absolute', left: 0, top: 0, width: 108, height: 64,
    borderRadius: 8, overflow: 'hidden', background: 'var(--offWhite)',
    border: '2px solid #fff', boxShadow: '0 2px 8px rgba(0,0,0,.18)',
    transformOrigin: 'center',
  },
  thumbImg: { width: '100%', height: '100%', objectFit: 'cover', display: 'block' },
  play: {
    position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%,-50%)',
    width: 24, height: 24, borderRadius: '50%', background: 'rgba(0,0,0,.62)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    paddingLeft: 2,
  },
  body: { minWidth: 0 },
  eyebrowRow: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6, flexWrap: 'wrap' },
  eyebrow: { fontSize: 11, letterSpacing: '.16em', textTransform: 'uppercase', color: 'var(--brandRed)', fontWeight: 700 },
  live: { display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11, fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase', color: 'var(--brandRed)', background: 'rgba(219,30,23,.08)', borderRadius: 999, padding: '3px 9px' },
  liveDot: { width: 7, height: 7, borderRadius: '50%', background: 'var(--brandRed)', display: 'inline-block' },
  headline: { fontFamily: 'var(--font-display)', fontSize: 22, lineHeight: 1.22, fontWeight: 700, color: 'var(--fg-1)', marginBottom: 6, textWrap: 'balance' },
  sub: { fontSize: 14, lineHeight: 1.5, color: 'var(--grey)', textWrap: 'pretty' },
  ctaCol: { display: 'flex', alignItems: 'center', gap: 8, color: 'var(--brandRed)', flexShrink: 0, paddingLeft: 8 },
  cta: { fontSize: 14, fontWeight: 600, whiteSpace: 'nowrap' },
  arrow: { fontSize: 18, lineHeight: 1 },

  /* Inline-video card (Welsh / Japanese) */
  inlineCard: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1.1fr) minmax(0, 1fr)',
    gap: 32,
    alignItems: 'center',
    background: '#fff',
    border: '1px solid var(--border)',
    borderRadius: 16,
    padding: '24px',
    maxWidth: 980,
    margin: '0 auto',
    boxShadow: 'var(--shadow-sm)',
  },
  inlinePlayer: { position: 'relative', width: '100%', aspectRatio: '16 / 9', borderRadius: 10, overflow: 'hidden', background: '#000' },
  inlinePoster: {
    position: 'absolute', inset: 0, width: '100%', height: '100%', padding: 0,
    border: 0, background: 'transparent', cursor: 'pointer', display: 'block',
  },
  inlinePosterImg: { width: '100%', height: '100%', objectFit: 'cover', display: 'block' },
  inlinePosterShade: {
    position: 'absolute', inset: 0,
    background: 'linear-gradient(180deg, rgba(0,0,0,0) 50%, rgba(0,0,0,.35) 100%)',
    pointerEvents: 'none',
  },
  inlinePlayBtn: {
    position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%, -50%)',
    width: 64, height: 64, borderRadius: '50%', background: 'rgba(219,30,23,.92)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    paddingLeft: 4, boxShadow: '0 6px 18px rgba(0,0,0,.4)',
  },
  inlineIframe: { width: '100%', height: '100%', border: 0, display: 'block' },
  inlineBody: { minWidth: 0, padding: '0 8px' },
  inlineHeadline: { fontFamily: 'var(--font-display)', fontSize: 28, lineHeight: 1.18, fontWeight: 700, color: 'var(--fg-1)', margin: '8px 0 12px', textWrap: 'balance' },
  inlineSub: { fontSize: 15, lineHeight: 1.55, color: 'var(--grey)', textWrap: 'pretty', marginBottom: 18 },
  inlineWatchCta: {
    appearance: 'none', background: 'transparent', border: 0, padding: 0,
    color: 'var(--brandRed)', fontSize: 14, fontWeight: 600, cursor: 'pointer',
    display: 'inline-flex', alignItems: 'center', gap: 8,
  },
};

function IclInlineVideo({ v, bg, pad, label }) {
  const { id, title } = v.inlineVideo;
  return (
    <section
      data-screen-label={label || 'See it in action'}
      style={{ background: bg, padding: pad, borderTop: '1px solid var(--border)', borderBottom: '1px solid var(--border)' }}
    >
      <div className="ssi-icl-inline" style={iclStyles.inlineCard}>
        <div style={iclStyles.inlinePlayer}>
          <iframe
            src={`https://www.youtube.com/embed/${id}?rel=0&modestbranding=1&playsinline=1`}
            title={title}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            referrerPolicy="strict-origin-when-cross-origin"
            allowFullScreen
            loading="lazy"
            style={iclStyles.inlineIframe}
          />
        </div>
        <div style={iclStyles.inlineBody}>
          <div style={iclStyles.eyebrow}>{v.eyebrow}</div>
          <div style={iclStyles.inlineHeadline}>{v.headline}</div>
          <div style={iclStyles.inlineSub}>{v.sub}</div>
        </div>
      </div>
    </section>
  );
}

export { IntensiveCrossLink };
